import React, { useState } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";

import { IRhytNameData, ITaskEditorProps } from "../../../../../../common/types";
import { setItem, hasItem } from "../../../../../cookie";
import { ICheckboxEvent } from "../../../../../types";
import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";

import EditorBar, {
    FlexLeft,
    FlexRight,
    Center,
    Changer,
} from "../../../EditorBar";
import ScoreEditor from "../../../../Score";
import { Checkbox } from "../../../../Form";

import getScore from "../../Common/getScore";
import getScale from "../../Common/getScale";
import getTempo from "../../Common/getTempo";
import ChangeColor from "../../Common/ChangeColor";
import ChangeScale from "../../Common/ChangeScale";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";

import messages from "./messages";
import { useUser } from "../../../../../store/user/selectors";
import Icon from "../../../../../icon";
import { Label } from "../../common-style";
import RhythmSyllableAudioUploader from "./RhythmSyllableAudioUploader";

const RhytName = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const user = useUser();
    const [isUploadSyllableAudioOpen, setIsUploadSyllableAudioOpen] = useState(false);
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    const changeHint = (e: ICheckboxEvent) => {
        let hint = e.target.checked;
        dispatch(editTask({ taskIndex, changingProp: "hint", val: hint }));
        dispatch(post("rhyt-name-hint", {
            planId: plan._id,
            index: taskIndex,
            hint,
        }));
    };

    const handleOpenSyllableAudioUploader = () => {
        setIsUploadSyllableAudioOpen(true);
    }
    const handleCloseSyllableAudioUploader = () => {
        setIsUploadSyllableAudioOpen(false);
    }

    const task = plan.tasks[taskIndex];
    const data = task.data as IRhytNameData;
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    };

    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="rhytName"
                    score={JSON.parse(JSON.stringify(getScore(plan, task) || {}))}
                    scale={getScale(plan, task)}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        {task?.data && <ChangeScale {...forwardProps} />}
                        {user.isGameAdmin && (
                            <div onClick={handleOpenSyllableAudioUploader}>
                                <Changer>
                                    <div>
                                        <Icon i="upload-audio" />{" "}
                                        <Label style={{ lineHeight: "normal" }}>
                                            <FormattedMessage {...messages.uploadAudio} />
                                        </Label>
                                    </div>
                                </Changer>
                            </div>
                        )}
                        {isUploadSyllableAudioOpen && (
                            <RhythmSyllableAudioUploader
                                close={handleCloseSyllableAudioUploader}
                                plan={plan}
                                taskIndex={taskIndex}
                            />
                        )}
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("rhytNameDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn(
                            !(inlineEditorOpen && !playable) && !hasItem("rhytNameDone") && !task.ref && "pulsate"
                        )}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <Changer>
                            <Checkbox
                                dark
                                label={<FormattedMessage {...messages.firstKnown} />}
                                checked={data.hint === undefined ? true : !!data.hint}
                                onChange={changeHint}
                            ></Checkbox>
                        </Changer>
                        <ChangeTempo {...forwardProps} />
                        <ChangeColor
                            {...forwardProps}
                            color={data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default RhytName;
